import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useOrganization } from "../context/OrganizationContext";
import stripeService from "../services/stripeService";

function periodEndUnix(subscription, stripeLive) {
  if (stripeLive?.current_period_end != null) {
    return Number(stripeLive.current_period_end);
  }
  if (subscription?.current_period_end) {
    const t = new Date(subscription.current_period_end).getTime();
    if (!Number.isNaN(t)) return Math.floor(t / 1000);
  }
  return null;
}

export default function SubscriptionStatusBanner() {
  const { subscription, refreshSubscription } = useOrganization();
  const [stripeLive, setStripeLive] = useState(null);

  useEffect(() => {
    if (!subscription?.stripe_subscription_id || !subscription?.id) {
      setStripeLive(null);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const data = await stripeService.getSubscriptionStatus(subscription.id);
        if (!cancelled) setStripeLive(data);
      } catch {
        if (!cancelled) setStripeLive(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    subscription?.id,
    subscription?.stripe_subscription_id,
    subscription?.cancel_at_period_end,
    subscription?.current_period_end,
  ]);

  const cancelAtEnd =
    subscription?.cancel_at_period_end ||
    stripeLive?.cancel_at_period_end === true;

  const endUnix = useMemo(
    () => periodEndUnix(subscription, stripeLive),
    [subscription, stripeLive],
  );

  useEffect(() => {
    if (!cancelAtEnd || !subscription?.id) return undefined;
    const t = setInterval(() => {
      refreshSubscription();
    }, 120000);
    return () => clearInterval(t);
  }, [cancelAtEnd, subscription?.id, refreshSubscription]);

  if (!subscription || !cancelAtEnd || !endUnix) return null;

  const endMs = endUnix * 1000;
  const daysLeft = Math.max(
    0,
    Math.ceil((endMs - Date.now()) / 86400000),
  );
  const endLabel = new Date(endMs).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });

  return (
    <div className="sticky top-0 z-40 border-b border-amber-500/25 bg-amber-500/10 px-4 py-3 text-center sm:px-6">
      <p className="font-secondary text-sm text-amber-100/95">
        <span className="font-semibold text-amber-50">Subscription ending</span>
        {" — "}
        Your plan stays active until{" "}
        <span className="font-semibold text-white">{endLabel}</span>
        {daysLeft > 0 ? (
          <>
            {" "}
            (<span className="tabular-nums">{daysLeft}</span> day
            {daysLeft === 1 ? "" : "s"} left)
          </>
        ) : null}
        .{" "}
        <Link
          to="/settings"
          className="font-semibold text-primary underline decoration-primary/40 underline-offset-2 hover:text-primary-hover"
        >
          Manage subscription
        </Link>
      </p>
    </div>
  );
}
